import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Phone,
  ChevronDown,
  CheckCircle2,
  Laptop,
  Mail,
  ShieldCheck,
  Wifi,
  FileCheck,
  Database,
  UserRound,
  Building2,
  Briefcase,
  MapPin,
  ArrowRight,
  Scale,
  Gavel,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getPostsByTag } from "@/data/blog-posts";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

const LOCATIONS = [
  "Montclair, CA",
  "Rancho Cucamonga, CA",
  "Ontario, CA",
  "Upland, CA",
  "Claremont, CA",
  "Chino, CA",
  "Pomona, CA",
  "Inland Empire & Orange County",
];

const TITLE = "Tech Support for Estate Planning Attorneys | SimpleTech";
const DESCRIPTION =
  "Reliable IT support for estate planning attorneys. Legacy software integration, data security, and compliance from SimpleTech. Call (909) 255-9112.";
const CANONICAL = canonical("/services/tech-support-for-estate-planning-attorneys");

const FAQS = [
  {
    q: "Can you help us modernize without replacing the software our firm depends on?",
    a: "Yes — and this is one of our specialties. SimpleTech integrates your existing legacy software with modern tools and operating systems so your practice keeps everything it relies on, while gaining the speed and reliability of updated technology.",
  },
  {
    q: "How do you protect attorney-client confidentiality from a technology standpoint?",
    a: "SimpleTech implements encrypted storage, multi-factor authentication, secure remote access, access controls, and regular security audits — ensuring your client data is protected at every layer. We treat confidentiality as a technical requirement, not an afterthought.",
  },
  {
    q: "Do you support legal practice management software like Clio, WealthCounsel, or HotDocs?",
    a: "Yes. SimpleTech provides setup, integration, and troubleshooting support for legal and estate planning platforms. If your software needs to connect with other tools, we make that happen cleanly and reliably.",
  },
  {
    q: "What if our computers are old — is it worth updating or should we replace them?",
    a: "SimpleTech will assess your current hardware honestly and give you a clear recommendation. Often, a targeted update and performance optimization extends the useful life of existing machines by years. When replacement is the right call, we manage the full transition with zero data loss.",
  },
  {
    q: "Do you offer ongoing IT support or just one-time fixes?",
    a: "Both. SimpleTech offers one-time project support and ongoing managed IT plans for law firms that want a reliable technology partner — not just someone to call when things break. Proactive maintenance prevents most issues before they ever affect your practice.",
  },
];

export const Route = createFileRoute("/services_/tech-support-for-estate-planning-attorneys")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Tech Support for Estate Planning Attorneys",
          description:
            "IT support and tech modernization for estate planning attorneys and law firms — legacy software integration, data security, document management, and ongoing managed IT.",
          provider: {
            "@type": "LocalBusiness",
            name: "SimpleTech",
            telephone: "+1-909-255-9112",
            url: SITE_URL,
            address: {
              "@type": "PostalAddress",
              streetAddress: "8880 N Benson Ave #121",
              addressLocality: "Montclair",
              addressRegion: "CA",
              postalCode: "91763",
              addressCountry: "US",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
    ],
  }),
  component: EstatePlanningTechSupportPage,
});

const SERVICES = [
  {
    Icon: Database,
    title: "Legacy Software Integration & Modernization",
    body: "Many estate planning attorneys have invested years in practice management platforms, document assembly tools, and client databases that can't simply be replaced. SimpleTech bridges the gap — integrating your existing legacy software with modern operating systems, cloud tools, and productivity platforms so everything works together. You keep the specialized tools you depend on; we make sure they run the way they should.",
  },
  {
    Icon: Laptop,
    title: "Computer Updates & Performance Optimization",
    body: "Slow, outdated computers create delays that compound throughout the day — slower document loading, longer client wait times, and increased frustration for staff. SimpleTech updates hardware and software, optimizes system performance, and ensures every workstation in your office is running at full speed. Many attorneys are surprised at how much time they reclaim after a proper system refresh.",
  },
  {
    Icon: FileCheck,
    title: "Document Management & Workflow Optimization",
    body: "Estate planning practices live and die by their documents. Wills, trusts, healthcare directives, financial powers of attorney — every document needs to be organized, accessible, version-controlled, and secure. SimpleTech sets up and optimizes document management systems, cloud storage structures, and workflow automations that reduce manual work and eliminate the risk of lost or misfiled documents.",
  },
  {
    Icon: ShieldCheck,
    title: "Data Security & Client Confidentiality",
    body: "Attorney-client privilege isn't just a legal concept — it's a technical obligation. A breach of client data can trigger state bar complaints, malpractice exposure, and client loss that no practice fully recovers from. SimpleTech implements layered security including encrypted storage, multi-factor authentication, secure remote access, malware protection, and regular backup testing.",
  },
  {
    Icon: Gavel,
    title: "Practice Management Software Support",
    body: "Whether your firm uses Clio, MyCase, WealthCounsel, HotDocs, or another legal or estate planning platform, SimpleTech provides hands-on setup, integration, and troubleshooting support. We keep your practice management tools connected, updated, and working the way your workflow demands — so your team spends time on billable work, not tech problems.",
  },
  {
    Icon: Mail,
    title: "Email, Communication & Remote Work Setup",
    body: "We configure and support Google Workspace and Microsoft 365, set up encrypted email for client communications, sync calendars across devices, and enable secure remote access so attorneys can work from home, court, or client meetings without compromising confidentiality.",
  },
  {
    Icon: Wifi,
    title: "Network Security & Office Infrastructure",
    body: "A law office network requires more than a fast Wi-Fi connection. SimpleTech configures your network with proper segmentation, firewall protection, and access controls — ensuring that sensitive client files are protected from both external threats and accidental internal exposure. We also handle printer setup, scanner integration, and the office infrastructure that keeps daily operations moving.",
  },
];

const WHY_POINTS = [
  "Legacy software preserved — modern tools integrated seamlessly",
  "Same-day response — most issues resolved within hours",
  "Encrypted storage, MFA & access controls to protect privilege",
  "Practice management software (Clio, WealthCounsel, HotDocs) supported",
  "Plain-English explanations — no tech jargon",
  "A real local team that picks up the phone",
];

function EstatePlanningTechSupportPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader active="services" />
      <main id="main-content" className="flex-1">
        {/* Hero */}
        <section className="relative bg-[#0b0a3d] text-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0b0a3d] via-[#141432] to-[#1e1e5a] opacity-95" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 md:py-28">
            <p className="text-sky-400 font-bold tracking-widest text-sm uppercase mb-4">
              Estate Planning Attorney Tech Support
            </p>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Tech Support for Estate Planning Attorneys
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-bold text-sky-400">
              Secure. Compliant. Modernized — without losing what works.
            </p>
            <p className="mt-8 text-base md:text-lg text-white/85 leading-relaxed max-w-3xl">
              Estate planning attorneys handle some of life's most sensitive decisions — wills, trusts, powers of
              attorney, and the careful transfer of generational wealth. SimpleTech provides{" "}
              <strong className="text-white">dedicated tech support for estate planning attorneys</strong> — from
              modernizing legacy software and optimizing workflows to locking down client data and keeping your
              practice running without disruption.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <a
                href="tel:+19092559112"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                <Phone className="w-4 h-4" /> CALL (909) 255-9112
              </a>
              <Link
                to="/contact"
                className="inline-block bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                REQUEST SUPPORT
              </Link>
            </div>
            <p className="mt-5 text-sm text-white/70">Fast, local support for legal professionals.</p>
          </div>
        </section>

        {/* Why */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Why Estate Planning Attorneys Need Specialized IT Support
            </h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              Most IT companies treat a law office like any other small business. But estate planning practices have
              specific technology challenges generic support doesn't address. Your practice management software may be
              years old but deeply embedded in how you work. Your document templates, client files, and signing
              workflows are irreplaceable. Your state bar requires confidentiality safeguards that go well beyond
              basic antivirus. When a system goes down mid-appointment, it damages the professional image you've built
              and disrupts the client experience at the worst possible moment.
            </p>
            <ul className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {WHY_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-800">
                  <CheckCircle2 className="w-5 h-5 text-sky-500 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Case study */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-sky-600 font-bold tracking-widest text-sm uppercase mb-4">Real Results</p>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Modernizing an Estate Planning Practice Without Losing What Works
            </h2>
            <div className="mt-6 space-y-5 text-slate-700 text-base md:text-lg leading-relaxed">
              <p>
                One of our estate planning clients was running on outdated software that had become a daily obstacle.
                Their computers were slow, their systems weren't communicating with each other, and the technology
                meant to support their practice was holding it back instead.
              </p>
              <p>
                SimpleTech stepped in with a clear plan. We updated their computers, optimized their entire technology
                workflow, and — critically — ensured their legacy software integrated seamlessly with the modern tools
                their team needed. The practice didn't lose any of the specialized tools they'd relied on for years.
                They gained speed, reliability, and peace of mind.
              </p>
              <p className="font-semibold text-[#0b0a3d]">
                Today, that firm focuses entirely on their clients. The technology just works — quietly, reliably, in
                the background — the way it always should have.
              </p>
            </div>
          </div>
        </section>

        {/* Services grid */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Tech Support Services for Estate Planning Attorneys
            </h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech provides comprehensive IT support for estate planning law firms — covering everything from
              legacy systems to day-to-day tech operations.
            </p>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {SERVICES.map(({ Icon, title, body }) => (
                <div key={title} className="bg-slate-50 rounded-lg p-7 shadow-sm hover:shadow-md transition-shadow">
                  <Icon className="w-10 h-10 text-sky-500" strokeWidth={1.5} />
                  <h3 className="mt-5 text-xl font-extrabold text-[#0b0a3d]">{title}</h3>
                  <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 text-slate-700 text-[15px] leading-relaxed max-w-3xl space-y-3">
              <p>
                Looking for a complete IT partner? See our{" "}
                <Link to="/services/managed-it-services" className="text-sky-600 font-semibold hover:underline">
                  managed IT services
                </Link>{" "}
                for end-to-end coverage, or our{" "}
                <Link
                  to="/services/$slug"
                  params={{ slug: "cyber-security" }}
                  className="text-sky-600 font-semibold hover:underline"
                >
                  cybersecurity services
                </Link>{" "}
                to protect privileged client data and meet compliance requirements.
              </p>
              <p>
                Need a reliable phone system too? See our{" "}
                <Link
                  to="/services/$slug"
                  params={{ slug: "voip" }}
                  className="text-sky-600 font-semibold hover:underline"
                >
                  VoIP solutions for law firms
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Who we support */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">Who We Support</h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech provides law firm tech support for:
            </p>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { Icon: UserRound, label: "Solo estate planning attorneys" },
                { Icon: Building2, label: "Small estate & trust law firms (2–15 attorneys)" },
                { Icon: Briefcase, label: "Multi-practice firms with estate planning departments" },
                { Icon: Scale, label: "Elder law & Medicaid planning practices" },
                { Icon: Gavel, label: "Probate & trust administration offices" },
                { Icon: FileCheck, label: "Paralegals, legal staff & office administrators" },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-4 p-5 rounded-md border border-slate-200 bg-white">
                  <Icon className="w-8 h-8 text-sky-500 shrink-0" strokeWidth={1.5} />
                  <span className="font-semibold text-[#0b0a3d]">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Locations We Serve */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">Locations We Serve</h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech supports law firms across Southern California — with same-day remote help everywhere we
              work, and on-site visits across the areas below.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {LOCATIONS.map((loc) => (
                <div
                  key={loc}
                  className="flex items-center gap-3 bg-slate-50 rounded-md border border-slate-200 px-4 py-3"
                >
                  <MapPin className="w-5 h-5 text-sky-500 shrink-0" />
                  <span className="font-semibold text-[#0b0a3d] text-sm">{loc}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-slate-700 leading-relaxed max-w-3xl">
              Don't see your city?{" "}
              <a href="tel:+19092559112" className="text-sky-600 font-semibold hover:underline">
                Call (909) 255-9112
              </a>{" "}
              — we likely cover it too.
            </p>
          </div>
        </section>

        {/* From the Blog — auto-pulls posts tagged "estate-planning" or "legal" */}
        <BlogSection />

        {/* CTA band */}
        <section className="bg-[#0b0a3d] text-white py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
              Modernize Your Practice — Without Losing What Works
            </h2>
            <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">
              Your clients trust you with their legacies. Your technology should honor that trust — not undermine it.
              Whether you need to modernize aging systems, secure client data, or find an IT partner who understands
              how a legal practice operates, SimpleTech is ready to help.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href="tel:+19092559112"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                <Phone className="w-4 h-4" /> CALL (909) 255-9112
              </a>
              <Link
                to="/contact"
                className="inline-block bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                CONTACT US
              </Link>
            </div>
            <p className="mt-6 text-sm text-white/70">No rushing. No jargon. A team that answers the phone.</p>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight text-center">
              Frequently Asked Questions
            </h2>
            <div className="mt-12 space-y-3">
              {FAQS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={f.q} className="border border-slate-200 rounded-md overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-slate-50 transition-colors"
                      aria-expanded={open}
                    >
                      <span className="font-bold text-[#0b0a3d]">{f.q}</span>
                      <ChevronDown
                        className={`w-5 h-5 text-sky-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                      />
                    </button>
                    {open && <div className="px-5 pb-5 text-slate-700 leading-relaxed">{f.a}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function BlogSection() {
  const tagged = getPostsByTag("estate-planning");
  const legal = getPostsByTag("legal");
  const merged = [...tagged, ...legal.filter((p) => !tagged.some((t) => t.slug === p.slug))];
  if (merged.length === 0) return null;
  return (
    <section className="bg-slate-50 py-20 md:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Estate Planning & Legal Tech Insights
            </h2>
            <p className="mt-4 text-slate-700 text-base md:text-lg leading-relaxed max-w-2xl">
              Guides, tips, and updates for estate planning attorneys and law firms — from our blog.
            </p>
          </div>
          <Link to="/articles" className="inline-flex items-center gap-2 text-sky-600 font-bold hover:text-sky-700">
            All articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {merged.slice(0, 6).map((post) => (
            <Link
              key={post.slug}
              to="/articles/$slug"
              params={{ slug: post.slug }}
              className="group bg-white rounded-lg overflow-hidden border border-slate-200 hover:shadow-lg transition-shadow"
            >
              <div className="aspect-[16/9] overflow-hidden bg-slate-200">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <p className="text-xs font-bold text-sky-600 tracking-wider uppercase">{post.date}</p>
                <h3 className="mt-2 text-lg font-extrabold text-[#0b0a3d] group-hover:text-sky-600 transition-colors leading-tight">
                  {post.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">{post.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
